/**
 * P0-16 · Client-side error reporting.
 *
 * Ships a minimal error description to the server intake (`/api/observability`),
 * which attaches store/requestId and scrubs PII before forwarding to the sink.
 * Best-effort and non-throwing — reporting must never break the error UI.
 */

export interface ClientErrorPayload {
  message: string;
  stack?: string;
  name?: string;
  route?: string;
  /** Next.js error `digest`, correlating the client error to the server log. */
  digest?: string;
}

export function reportClientError(payload: ClientErrorPayload): void {
  try {
    const body = JSON.stringify(payload);
    if (new Blob([body]).size > 48_000) return;
    // sendBeacon survives navigation/unmount; fall back to fetch(keepalive).
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        if (navigator.sendBeacon("/api/observability", body)) return;
      } catch { /* A refused or throwing beacon still has the fetch fallback. */ }
    }
    void fetch("/api/observability", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // swallow — never let reporting throw inside an error boundary
  }
}
